import React from 'react';
import {
  Target,
  ChartBar,
  FileText,
  ArrowRight,
  CircleDollarSign,
  Users,
  Building2,
  GitBranch,
  Globe,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Workflow
} from 'lucide-react';

// Composants réutilisés de l'analyse précédente
const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`mb-8 bg-white rounded-xl border border-gray-200 ${className}`}>
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-center">
        <Icon className="w-6 h-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const AnalysisPoint = ({ title, status = "info", children }) => {
  const statusColors = {
    success: "border-green-500 bg-green-50",
    warning: "border-yellow-500 bg-yellow-50",
    error: "border-red-500 bg-red-50",
    info: "border-blue-500 bg-blue-50"
  };

  return (
    <div className={`border-l-4 rounded-r-lg p-4 ${statusColors[status]}`}>
      <h3 className="font-medium mb-2">{title}</h3>
      <div className="text-sm space-y-2">{children}</div>
    </div>
  );
};

const Metric = ({ label, value, trend }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <div className="text-sm text-gray-600 mb-1">{label}</div>
    <div className="text-2xl font-semibold">{value}</div>
    {trend && (
      <div className="text-sm text-gray-500 mt-1">{trend}</div>
    )}
  </div>
);

const GIMACAnalysis = () => {
  // Contexte et Objectifs
  const projectContext = {
    vision: "Modernisation du système monétique régional CEMAC",
    objectives: [
      "Mise en place d'un switch régional nouvelle génération",
      "Interopérabilité des services financiers numériques",
      "Réduction des coûts de transaction transfrontaliers",
      "Amélioration de l'inclusion financière régionale"
    ],
    scope: {
      geographical: ["6 pays CEMAC"],
      institutions: [
        "47 institutions financières",
        "Banques commerciales",
        "Établissements de microfinance",
        "Établissements de monnaie électronique"
      ],
      services: [
        "Paiements instantanés",
        "Mobile Money",
        "Cartes bancaires",
        "Transferts transfrontaliers"
      ]
    }
  };

  // Analyse Marché & Impact
  const marketAnalysis = {
    marketSize: {
      participants: "47 institutions financières",
      coverage: "6 pays CEMAC",
      potential: "Population totale 50M+"
    },
    stakeholders: [
      {
        type: "Régulateurs",
        entities: ["BEAC", "COBAC"],
        expectations: ["Conformité réglementaire", "Supervision en temps réel", "Reporting standardisé"]
      },
      {
        type: "Institutions financières",
        entities: ["Banques", "EMF", "EME"],
        expectations: ["Réduction des coûts", "Facilité d'intégration", "Fiabilité système"]
      }
    ]
  };

  // Analyse Technique
  const technicalAnalysis = {
    architecture: {
      components: [
        {
          name: "Switch Régional",
          description: "Plateforme centrale de routage",
          tech: ["Processing temps réel", "APIs modernes", "Monitoring avancé"],
          status: "Phase 1 terminée"
        },
        {
          name: "Mobile Money Hub",
          description: "Interopérabilité services financiers mobiles",
          tech: ["ISO 20022", "APIs REST", "Temps réel"],
          status: "En développement"
        }
      ],
      standards: [
        {
          name: "ISO 20022",
          scope: "Messagerie financière",
          status: "En implémentation"
        }
      ]
    },
    challenges: [
      {
        category: "Infrastructure",
        points: [
          "Connectivité réseau intermittente",
          "Hétérogénéité des systèmes existants",
          "Contraintes de performance"
        ]
      },
      {
        category: "Réglementaire",
        points: [
          "Conformité réglementations CEMAC",
          "Reporting régulateurs",
          "Gestion des risques"
        ]
      }
    ]
  };

  // Plan d'implémentation
  const implementationPlan = {
    phases: [
      {
        name: "Phase 1 - Fondation",
        timeline: "2023-2024",
        deliverables: [
          "Switch central nouvelle génération",
          "Intégration participants pilotes",
          "Monitoring basique"
        ],
        status: "Terminé"
      },
      {
        name: "Phase 2 - Extension",
        timeline: "2024-2025",
        deliverables: [
          "Interopérabilité Mobile Money",
          "Paiements instantanés",
          "Reporting avancé"
        ],
        status: "En cours"
      }
    ],
    risks: [
      {
        risk: "Adhésion participants",
        impact: "Élevé",
        mitigation: "Programme accompagnement"
      },
      {
        risk: "Stabilité technique",
        impact: "Élevé",
        mitigation: "Tests exhaustifs"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Analyse Détaillée - Modernisation GIMAC CEMAC
          </h1>
          <div className="flex space-x-4">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Phase: Extension
            </span>
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
              Budget: 95M€
            </span>
          </div>
        </div>

        {/* Vision et Contexte */}
        <Section title="Vision et Contexte" icon={Target}>
          <div className="space-y-6">
            <AnalysisPoint title="Vision Stratégique">
              <p>{projectContext.vision}</p>
              <div className="mt-4">
                <h4 className="font-medium mb-2">Objectifs Clés:</h4>
                <ul className="list-disc pl-4 space-y-1">
                  {projectContext.objectives.map((obj, idx) => (
                    <li key={idx}>{obj}</li>
                  ))}
                </ul>
              </div>
            </AnalysisPoint>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <h4 className="font-medium mb-2">Couverture</h4>
                <ul className="space-y-1">
                  {projectContext.scope.geographical.map((geo, idx) => (
                    <li key={idx} className="flex items-center">
                      <Globe className="w-4 h-4 mr-2 text-blue-500" />
                      {geo}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Institutions</h4>
                <ul className="space-y-1">
                  {projectContext.scope.institutions.map((inst, idx) => (
                    <li key={idx} className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-blue-500" />
                      {inst}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Services</h4>
                <ul className="space-y-1">
                  {projectContext.scope.services.map((service, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* Analyse Technique */}
        <Section title="Analyse Technique" icon={GitBranch}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalAnalysis.architecture.components.map((comp, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium mb-2">{comp.name}</h3>
                  <p className="text-sm text-gray-600 mb-3">{comp.description}</p>
                  <div className="space-y-2">
                    <div>
                      <h4 className="text-xs font-medium mb-1">Technologies:</h4>
                      <div className="flex flex-wrap gap-2">
                        {comp.tech.map((t, i) => (
                          <span key={i} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-xs font-medium mb-1">Statut:</h4>
                      <span className={`px-2 py-1 rounded text-xs ${
                        comp.status.includes('terminée') 
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {comp.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {technicalAnalysis.challenges.map((challenge, idx) => (
                <AnalysisPoint 
                  key={idx} 
                  title={challenge.category}
                  status="warning"
                >
                  <ul className="space-y-1">
                    {challenge.points.map((point, i) => (
                      <li key={i} className="flex items-center">
                        <AlertTriangle className="w-4 h-4 mr-2 text-yellow-500" />
                        {point}
                      </li>
                    ))}
                  </ul>
                </AnalysisPoint>
              ))}
            </div>
          </div>
        </Section>

        {/* Plan d'implémentation */}
        <Section title="Plan d'Implémentation" icon={Workflow}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {implementationPlan.phases.map((phase, idx) => (
                <div key={idx} className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-medium">{phase.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      phase.status === 'Terminé' 
                        ? 'bg-green-100 text-green-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {phase.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Timeline: {phase.timeline}</p>
                  <div>
                    <h4 className="text-sm font-medium mb-2">Livrables:</h4>
                    <ul className="space-y-1">
                      {phase.deliverables.map((deliverable, i) => (
                        <li key={i} className="flex items-center text-sm">
                          <ArrowRight className="w-4 h-4 mr-2 text-gray-400" />
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <AnalysisPoint title="Gestion des Risques" status="warning">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {implementationPlan.risks.map((risk, idx) => (
                  <div key={idx} className="bg-white p-3 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium">{risk.risk}</span>
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                        Impact: {risk.impact}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Mitigation: {risk.mitigation}
                    </p>
                  </div>
                ))}
              </div>
            </AnalysisPoint>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default GIMACAnalysis;